/**
 * 奖品价值计算工具
 * 用于计算不同类型奖品的美元价值
 */

import type * as LuckyWheelTypes from "@/lib/idl/lucky_wheel_idl";
import { createLogAdapter } from "@/lib/logger/console-adapter";

const uiLog = createLogAdapter("app/plaza/lucky-wheel/utils/prizeValueCalculator.ts");


// BABYODIN 的 runeId（用于 Ticket 和 Balance 类型的换算）
const BABYODIN_RUNE_ID = "2rfi";

// 价格缓存（避免重复请求）
const priceCache = new Map<string, { price: number; timestamp: number }>();
const CACHE_DURATION = 5 * 60 * 1000; // 5分钟缓存

/**
 * 格式化大数字为 K/M 格式
 * @param num 数字
 * @returns 格式化后的字符串，如 "30K", "1.5M"
 */
export function formatLargeNumber(num: number): string {
	if (num >= 1_000_000) {
		const millions = num / 1_000_000;
		return millions % 1 === 0 ? `${millions}M` : `${millions.toFixed(1)}M`;
	} else if (num >= 1_000) {
		const thousands = num / 1_000;
		return thousands % 1 === 0 ? `${thousands}K` : `${thousands.toFixed(1)}K`;
	}
	return num.toString();
}

/**
 * 从 API 获取 Rune 价格（nano_sats）
 * @param runeId Rune ID
 * @returns 价格（nano_sats），失败返回 0
 */
export async function fetchRunePrice(runeId: string): Promise<number> {
	// 检查缓存
	const cached = priceCache.get(runeId);
	if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
		return cached.price;
	}

	try {
		const response = await fetch(`https://api.odin.fun/v1/token/${runeId}`, {
			method: "GET",
			headers: {
				"Content-Type": "application/json",
			},
		});

		if (!response.ok) {
			uiLog.error(
				`Failed to fetch rune price for ${runeId}: ${response.status}`,
			);
			return 0;
		}

		const data = await response.json();
		const price = data.price || 0;

		// 更新缓存
		priceCache.set(runeId, { price, timestamp: Date.now() });

		return price;
	} catch (error) {
		uiLog.error(`Error fetching rune price for ${runeId}:`, error);
		return 0;
	}
}

/**
 * 格式化美元价值
 * @param value 美元价值
 * @returns 格式化后的字符串，如 "$456.98"
 */
export function formatUsdValue(value: number): string {
	return `$${value.toFixed(2)}`;
}

/**
 * 计算奖品价值
 * @param prizeType 奖品类型
 * @param btcPrice BTC 当前美元价格
 * @returns 奖品价值信息
 */
export async function calculatePrizeValue(
	prizeType: LuckyWheelTypes.PrizeType,
	btcPrice: number,
): Promise<{
	formattedAmount: string; // 格式化后的数量，如 "30K BABYODIN"
	usdValue: number; // 美元价值
	rawAmount: number; // 原始数量
}> {
	let rawAmount = 0;
	let formattedAmount = "";
	let usdValue = 0;

	try {
		if ("Rune" in prizeType) {
			// Rune 类型
			const { amount, tokenInfo } = prizeType.Rune;
			rawAmount = Number(amount);
			const runeName = tokenInfo.runeName;

			// 格式化数量
			formattedAmount = `${formatLargeNumber(rawAmount)} ${runeName}`;

			// 获取价格并计算美元价值
			const price = await fetchRunePrice(tokenInfo.runeId);
			if (price > 0 && btcPrice > 0) {
				// BTC 价值 = amount * price / 1e11
				// USD 价值 = BTC 价值 * btcPrice
				const btcValue = (rawAmount * price) / 1e11;
				usdValue = btcValue * btcPrice;
			}
		} else if ("Ticket" in prizeType) {
			// Ticket 类型：1 Ticket = 100 BABYODIN
			const ticketCount = Number(prizeType.Ticket);
			rawAmount = ticketCount;
			formattedAmount = `${ticketCount} 符券`;

			// 换算成 BABYODIN 数量
			const babyOdinAmount = ticketCount * 100;
			const price = await fetchRunePrice(BABYODIN_RUNE_ID);
			if (price > 0 && btcPrice > 0) {
				const btcValue = (babyOdinAmount * price) / 1e11;
				usdValue = btcValue * btcPrice;
			}
		} else if ("Balance" in prizeType) {
			// Balance 类型：1 Balance = 1 BABYODIN
			const balance = Number(prizeType.Balance);
			rawAmount = balance;
			formattedAmount = `${balance} DD`;

			// 换算成 BABYODIN 数量
			const babyOdinAmount = balance;
			const price = await fetchRunePrice(BABYODIN_RUNE_ID);
			if (price > 0 && btcPrice > 0) {
				const btcValue = (babyOdinAmount * price) / 1e11;
				usdValue = btcValue * btcPrice;
			}
		}
	} catch (error) {
		uiLog.error("Error calculating prize value:", error);
	}

	return {
		formattedAmount,
		usdValue,
		rawAmount,
	};
}

/**
 * 批量计算奖品价值（可用于优化性能）
 * @param prizeTypes 奖品类型数组
 * @param btcPrice BTC 当前美元价格
 * @returns 奖品价值信息数组
 */
export async function calculatePrizeValuesBatch(
	prizeTypes: LuckyWheelTypes.PrizeType[],
	btcPrice: number,
): Promise<
	Array<{
		formattedAmount: string;
		usdValue: number;
		rawAmount: number;
	}>
> {
	// 收集所有需要的 runeId
	const runeIds = new Set<string>();
	prizeTypes.forEach((prizeType) => {
		if ("Rune" in prizeType) {
			runeIds.add(prizeType.Rune.tokenInfo.runeId);
		} else if ("Ticket" in prizeType || "Balance" in prizeType) {
			runeIds.add(BABYODIN_RUNE_ID);
		}
	});

	// 批量预加载价格
	await Promise.all(Array.from(runeIds).map((id) => fetchRunePrice(id)));

	// 计算所有价值
	return Promise.all(
		prizeTypes.map((prizeType) => calculatePrizeValue(prizeType, btcPrice)),
	);
}
